<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess, loadCurrentSubscription } from '@shared/Root/Context.access';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes, bsAlert } from '@shared/components/BsAlerts';
    import Form from '@shared/components/Form/Form.vue';
    import { FormEl, Data } from '@shared/components/Form/Form';
    import { getCurrentClientCompany, saveCurrentClientCompany, ClientCompany } from './AccountBillingDetails.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive<{
        isLoading: boolean;
        loadError: boolean;
        isSaving: boolean;
        company: ClientCompany | null;
        formData: FormEl;
        setInitData: boolean;
    }>({
        isLoading: true,
        loadError: false,
        isSaving: false,
        company: null,
        formData: {},
        setInitData: false,
    });

    onMounted(async () => {
        try {
            if (!contextAccess.state.currentSubscription) {
                contextAccess.state.currentSubscription = await loadCurrentSubscription();
            }

            state.company = await getCurrentClientCompany();

            setupForm();
        } catch {
            state.loadError = true;
        } finally {
            state.isLoading = false;
        }
    });

    function paymentMethodLabel(): string {
        const methods: Record<string, string> = {
            CreditCard: $Resources.BillingMethodCreditCard,
            EmailInvoice: $Resources.BillingMethodEmailInvoice,
            PaperInvoice: $Resources.BillingMethodPaperInvoice,
            EInvoice: $Resources.BillingMethodEInvoice,
        };

        const method = contextAccess.state.currentSubscription?.DefaultPaymentMethod;
        return (method && methods[method]) || method || '';
    }

    function setupForm(): void {
        const company = state.company!;
        const subscription = contextAccess.state.currentSubscription;

        state.formData = {
            BillingName: {
                modelValue: company.BillingName,
                input: {
                    label: $Resources.CompanyNameLabel,
                    placeholder: $Resources.CompanyBillingNamePlaceholder,
                    infoText: $Resources.CompanyBillingNameLabel.replace('{0}', company.Name),
                },
            },
            BillingAttention: {
                modelValue: company.BillingAttention,
                input: { label: $Resources.BillingAttentionLabel, placeholder: $Resources.BillingAttentionPlaceholder },
            },
            BillingRecipientName: {
                modelValue: company.BillingRecipientName,
                input: { label: $Resources.BillingRecipientNameLabel, placeholder: $Resources.BillingRecipientNamePlaceholder },
            },
            BillingAddress: {
                modelValue: company.BillingAddress,
                textArea: { label: $Resources.CompanyBillingAddressLabel, placeholder: $Resources.CompanyBillingAddressPlaceholder, rows: 1 },
            },
            BillingEmail: {
                modelValue: company.BillingEmail,
                required: !!company.BillingEmail,
                input: {
                    label: $Resources.CompanyBillingEmailLabel,
                    placeholder: $Resources.CompanyBillingEmailPlaceholder,
                    type: 'email',
                    invalidMessage: $Resources.PropertyClientCompanyBillingEmailFailureText,
                },
            },
            BillingPeriod: {
                modelValue: subscription ? `${subscription.PaymentPeriod} ${$Resources.RecurrenceLabelMonths}` : '',
                textOrHtmlText: { label: $Resources.BillingPeriodLabel },
            },
            BillingMethod: {
                modelValue: paymentMethodLabel(),
                textOrHtmlText: { label: $Resources.BillingMethodLabel },
            },
            Contact: {
                modelValue: company.Contact,
                input: { label: $Resources.CompanyContactLabel, placeholder: $Resources.CompanyContactPlaceholder },
            },
            Phone: {
                modelValue: company.Phone,
                inputTel: { label: $Resources.CompanyPhoneLabel },
            },
            Url: {
                modelValue: company.Url,
                input: { label: $Resources.CompanyUrlLabel, placeholder: $Resources.CompanyUrlPlaceholder },
            },
            VatNumber: {
                modelValue: company.VatNumber,
                textOrHtmlText: { label: $Resources.VatNumberLabel, placeholder: $Resources.ValueIsMissing },
            },
        };
    }

    async function saveCompany(_event: Event, data: Data): Promise<void> {
        if (!state.company || state.isSaving) return;

        state.isSaving = true;
        state.setInitData = false;

        const company: ClientCompany = {
            ...state.company,
            BillingName: data.BillingName,
            BillingAttention: data.BillingAttention,
            BillingRecipientName: data.BillingRecipientName,
            BillingAddress: data.BillingAddress,
            BillingEmail: data.BillingEmail,
            Contact: data.Contact,
            Phone: data.Phone,
            Url: data.Url,
        };

        try {
            state.company = await saveCurrentClientCompany(company);
            state.setInitData = true;

            bsAlert({ title: $Resources.AlertCompanySavedHeader, content: $Resources.AlertCompanySavedBody });
        } catch (error: any) {
            let message = error?.message;
            try {
                message = JSON.parse(message)?.Message ?? message;
            } catch (_) {}

            switch (message) {
                case 'Vat-Invalid':
                    bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorSavingCompanyBody });
                    break;
                case 'BillingEmail-CantRemove':
                    bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.PropertyClientCompanyBillingEmailFailureText });
                    break;
                default:
                    bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading });
            }
        } finally {
            state.isSaving = false;
        }
    }
</script>

<template>
    <div class="max-900">
        <h1 class="h2 m-lg-b">{{ $Resources.CompanyBillingDetailsHeader }}</h1>

        <Loading :show="state.isLoading" loadingStyle="progress-bar" fixed></Loading>

        <BsAlert
            v-if="state.loadError"
            :type="AlertTypes.Danger"
            :dismissible="false"
            :title="$Resources.ErrorHeading"
            :content="$Resources.ErrorContactSupport" />

        <Form
            class="wrapper-grid"
            v-if="!state.isLoading && !state.loadError"
            :data="state.formData"
            :submitting="state.isSaving"
            :setInitData="state.setInitData"
            :buttonText="$Resources.CompanyModalSaveButton"
            @update:modelValue="(key, newVal) => (state.formData[key].modelValue = newVal)"
            @update:isValid="(key, newVal) => (state.formData[key].isValid = newVal)"
            @onSubmit="saveCompany">
            <template #footer>
                <BsAlert
                    :type="AlertTypes.Info"
                    :dismissible="false"
                    :title="$Resources.ChangePaymentMethodInfoHeading"
                    :content="$Resources.ChangePaymentMethodInfoBody" />
            </template>
        </Form>
    </div>
</template>
